/**
 * @author    Nikko Renolds
 * @package   Trap Tycoon
 * @copyright (c) Fallox Developments 2026.
 * @license   MIT
 * @description Enhanced Ban Appeal interface with TTAC branding.
 */

import React, { useState } from "react";
import { ShieldAlert, Send, FileText, Lock, AlertTriangle } from "lucide-react";

export default function BanAppeal({ onAppeal, isSubmitted }) {
  const [reason, setReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) return;

    setIsSubmitting(true);
    await onAppeal(reason);
    setIsSubmitting(false);
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4 font-mono">
        <div className="max-w-lg w-full bg-zinc-950 border-2 border-yellow-600 p-8 text-center shadow-[0_0_50px_rgba(202,138,4,0.2)] animate-in fade-in zoom-in duration-500">
          <div className="flex justify-center mb-6">
            <div className="p-4 bg-yellow-900/20 rounded-full border-2 border-yellow-600 animate-pulse">
              <Lock size={48} className="text-yellow-500" />
            </div>
          </div>
          <h2 className="text-3xl font-black text-white italic uppercase mb-2">
            APPEAL UNDER REVIEW
          </h2>
          <p className="text-zinc-400 mb-6 text-sm leading-relaxed">
            Your case has been logged with the{" "}
            <span className="text-yellow-500 font-bold">
              TTAC Oversight Committee
            </span>
            . Do not spam requests. You will regain access if the restriction is
            lifted.
          </p>
          <div className="bg-black border border-zinc-800 p-4">
            <p className="text-[10px] text-zinc-600 uppercase tracking-widest font-bold">
              CASE STATUS: PENDING REVIEW
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4 font-mono relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(220,38,38,0.15)_0%,_transparent_70%)] pointer-events-none" />
      <div className="absolute top-0 left-0 w-full h-1 bg-red-600 z-10" />

      <div className="max-w-2xl w-full bg-zinc-950 border border-red-900/50 shadow-2xl relative z-20 flex flex-col md:flex-row">
        {/* Left Brand Panel */}
        <div className="bg-red-950/20 border-b md:border-b-0 md:border-r border-red-900/50 p-8 flex flex-col items-center justify-center text-center md:w-1/3">
          <ShieldAlert size={64} className="text-red-600 mb-4 animate-pulse" />
          <h1 className="text-2xl font-black text-white uppercase italic leading-none mb-2">
            TRAP TYCOON
            <br />
            <span className="text-red-600 text-lg">FLYTRAP</span>
          </h1>
          <p className="text-[10px] text-red-400 font-bold tracking-[0.2em] border-t border-red-900/50 pt-2 mt-2 w-full">
            TTAC SECURITY
          </p>
        </div>

        {/* Right Content Panel */}
        <div className="p-8 md:w-2/3">
          <div className="mb-6">
            <h2 className="text-xl font-bold text-white uppercase flex items-center gap-2">
              <AlertTriangle className="text-red-500" size={20} />
              Account Restricted
            </h2>
            <p className="text-xs text-zinc-500 mt-2 leading-relaxed">
              Your account has been flagged by the{" "}
              <strong>Trap Tycoon FlyTrap (TTAC)</strong> system for suspicious
              activity or policy violations. Access is currently suspended.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label className="text-[10px] uppercase font-bold text-zinc-400 flex justify-between">
                <span>Statement of Defense</span>
                <span className="text-zinc-600">{reason.length}/500</span>
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value.slice(0, 500))}
                className="w-full h-32 bg-black border border-zinc-800 p-3 text-sm text-zinc-300 focus:border-red-600 focus:ring-1 focus:ring-red-900 outline-none resize-none transition-all placeholder:text-zinc-700"
                placeholder="Explain the situation clearly. Admissions of guilt may lead to reduced sentences. Lies will result in a permanent ban."
              />
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-zinc-900">
              <a
                href="https://discord.gg/trap-tycoon"
                target="_blank"
                rel="noreferrer"
                className="text-[10px] text-zinc-500 hover:text-white flex items-center gap-1 transition-colors uppercase font-bold"
              >
                <FileText size={12} /> Read Ban Policy
              </a>

              <button
                type="submit"
                disabled={isSubmitting || !reason.trim()}
                className="bg-red-700 hover:bg-red-600 disabled:bg-zinc-800 disabled:text-zinc-600 text-white px-6 py-2 text-xs font-black uppercase italic tracking-wider flex items-center gap-2 transition-all"
              >
                {isSubmitting ? (
                  "Transmitting..."
                ) : (
                  <>
                    Submit Appeal <Send size={12} />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
